import { createDocxImagePagesBlob, createDocxTableBlob } from "./officeExport";

function normalizeText(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function directCellTexts(row) {
  return Array.from(row?.children || []).map((cell) => normalizeText(cell.textContent)).filter(Boolean);
}

function pushFieldRows(rows, section) {
  if (!section) return;
  const heading = normalizeText(section.querySelector("h3, h4")?.textContent);
  if (heading) rows.push([heading, ""]);
  section.querySelectorAll(".booking_fiscal_field_grid > div, .booking_fiscal_document_grid > div, .booking_fiscal_document_data_grid > div").forEach((field) => {
    const label = normalizeText(field.querySelector("span")?.textContent);
    const value = normalizeText(field.querySelector("strong, em")?.textContent);
    if (label || value) rows.push([label || "Dato", value || "-"]);
  });
}

function pushTableRows(rows, table, sectionLabel = "Dettaglio") {
  if (!table) return;
  const headers = Array.from(table.querySelectorAll("thead th")).map((cell) => normalizeText(cell.textContent));
  if (sectionLabel) rows.push([sectionLabel, ""]);
  table.querySelectorAll("tbody tr").forEach((row, index) => {
    const cells = directCellTexts(row);
    if (!cells.length) return;
    const value = cells.map((cell, cellIndex) => `${headers[cellIndex] || `Campo ${cellIndex + 1}`}: ${cell}`).join(" | ");
    rows.push([`Riga ${index + 1}`, value]);
  });
}

export function collectFiscalReportRows(root, title = "Documento fiscale") {
  if (!root) return [];
  const pages = root.matches?.(".booking_fiscal_report")
    ? [root]
    : Array.from(root.querySelectorAll?.(".booking_fiscal_report") || []);
  const effectivePages = pages.length ? pages : [root];
  const rows = [["Documento", normalizeText(title) || "Documento fiscale"]];

  effectivePages.forEach((page, pageIndex) => {
    if (effectivePages.length > 1) rows.push(["Pagina", `${pageIndex + 1} / ${effectivePages.length}`]);

    const company = page.querySelector(".booking_fiscal_company_identity, .booking_fiscal_company_header, .booking_fiscal_company_data");
    if (company) {
      const values = Array.from(company.querySelectorAll("strong, span, p"))
        .map((node) => normalizeText(node.textContent))
        .filter(Boolean);
      if (values.length) rows.push(["Emittente", [...new Set(values)].join(" · ")]);
    }

    page.querySelectorAll(".booking_fiscal_info_card_v2, .booking_fiscal_document_data_v2, .booking_fiscal_document_data_section").forEach((section) => pushFieldRows(rows, section));

    const linesTable = page.querySelector(".booking_fiscal_lines_table, .booking_fiscal_services_table, table");
    pushTableRows(rows, linesTable, "Dettaglio documento");

    const vatBox = page.querySelector(".booking_invoice_vat_box");
    if (vatBox) {
      rows.push(["Castelletto IVA", ""]);
      const header = Array.from(vatBox.querySelectorAll(".booking_invoice_vat_header span")).map((node) => normalizeText(node.textContent));
      vatBox.querySelectorAll(".booking_invoice_vat_row:not(.booking_invoice_vat_header)").forEach((row, index) => {
        const cells = Array.from(row.children).map((node) => normalizeText(node.textContent)).filter(Boolean);
        if (cells.length) rows.push([`IVA ${index + 1}`, cells.map((cell, cellIndex) => `${header[cellIndex] || `Campo ${cellIndex + 1}`}: ${cell}`).join(" | ")]);
      });
    }

    const totals = page.querySelector(".booking_fiscal_totals_panel_v2, .booking_fiscal_totals_panel");
    if (totals) {
      rows.push(["Totali", ""]);
      Array.from(totals.children).forEach((line) => {
        const label = normalizeText(line.querySelector("span")?.textContent);
        const value = normalizeText(line.querySelector("strong")?.textContent);
        if (label || value) rows.push([label || "Totale", value || "-"]);
      });
    }
  });

  return rows.filter((row, index) => index === 0 || row.some((cell) => normalizeText(cell)));
}

function cleanPdfText(value) {
  return normalizeText(value)
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/€/g, "EUR")
    .replace(/[^\x20-\x7E\xA0-\xFF]/g, "");
}

function pdfEscape(value) {
  return cleanPdfText(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

function wrapPdfText(value, maxChars = 90) {
  const text = cleanPdfText(value);
  if (!text) return [""];
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxChars) {
      current = next;
      return;
    }
    if (current) lines.push(current);
    if (word.length > maxChars) {
      for (let index = 0; index < word.length; index += maxChars) lines.push(word.slice(index, index + maxChars));
      current = "";
    } else current = word;
  });
  if (current) lines.push(current);
  return lines.length ? lines : [""];
}

export function createFiscalTextPdfBlob({ rows = [], title = "Documento fiscale", orientation = "portrait" } = {}) {
  const landscape = orientation === "landscape";
  const width = landscape ? 841.89 : 595.28;
  const height = landscape ? 595.28 : 841.89;
  const margin = 34;
  const maxChars = landscape ? 132 : 90;
  const usableHeight = height - margin * 2;
  const sourceRows = Array.isArray(rows) ? rows : [];
  const lines = [];
  sourceRows.forEach(([label, value]) => {
    const safeLabel = cleanPdfText(label);
    const safeValue = cleanPdfText(value);
    if (safeLabel && !safeValue) {
      lines.push({ text: safeLabel.toUpperCase(), bold: true, gapBefore: 6 });
      return;
    }
    const prefix = safeLabel ? `${safeLabel}: ` : "";
    wrapPdfText(`${prefix}${safeValue}`, maxChars).forEach((text, index) => lines.push({ text, bold: index === 0 && Boolean(safeLabel), gapBefore: index === 0 ? 1 : 0 }));
  });

  const lineHeight = 11;
  const titleBlock = 34;
  const maxLines = Math.max(12, Math.floor((usableHeight - titleBlock) / lineHeight));
  const pages = [];
  for (let index = 0; index < lines.length; index += maxLines) pages.push(lines.slice(index, index + maxLines));
  if (!pages.length) pages.push([{ text: "Nessun dato disponibile", bold: false }]);

  const objects = [];
  const addObject = (content) => { objects.push(content); return objects.length; };
  const fontRegular = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  const fontBold = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>");
  const pageIds = [];

  pages.forEach((pageLines, pageIndex) => {
    const commands = ["q", "1 1 1 rg", `0 0 ${width.toFixed(2)} ${height.toFixed(2)} re f`, "0 0 0 rg"];
    const text = (x, y, size, value, font = "F1") => commands.push(`BT /${font} ${size} Tf ${x.toFixed(2)} ${y.toFixed(2)} Td (${pdfEscape(value)}) Tj ET`);
    let y = height - margin;
    text(margin, y, 14, cleanPdfText(title).toUpperCase(), "F2");
    text(width - margin - 90, y, 8, `Pagina ${pageIndex + 1} / ${pages.length}`, "F2");
    y -= 14;
    commands.push(`0.82 G 0.7 w ${margin} ${y} m ${width - margin} ${y} l S 0 G`);
    y -= 16;
    pageLines.forEach((line) => {
      if (line.gapBefore) y -= line.gapBefore;
      text(margin, y, 8.4, line.text, line.bold ? "F2" : "F1");
      y -= lineHeight;
    });
    commands.push("Q");
    const stream = commands.join("\n");
    const contentId = addObject(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    const pageId = objects.length + 1;
    pageIds.push(pageId);
    addObject(`<< /Type /Page /Parent PAGES_PLACEHOLDER 0 R /MediaBox [0 0 ${width.toFixed(2)} ${height.toFixed(2)}] /Resources << /Font << /F1 ${fontRegular} 0 R /F2 ${fontBold} 0 R >> >> /Contents ${contentId} 0 R >>`);
  });

  const pagesId = objects.length + 1;
  for (let index = 0; index < objects.length; index += 1) objects[index] = objects[index].replace(/PAGES_PLACEHOLDER/g, String(pagesId));
  addObject(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`);
  const catalogId = addObject(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => { pdf += `${String(offset).padStart(10, "0")} 00000 n \n`; });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  const bytes = new Uint8Array(pdf.length);
  for (let index = 0; index < pdf.length; index += 1) bytes[index] = pdf.charCodeAt(index) & 0xff;
  return new Blob([bytes], { type: "application/pdf" });
}

export function createFiscalWordBlob({ rows = [], title = "Documento fiscale", orientation = "portrait" } = {}) {
  const normalizedRows = [["Sezione", "Valore"], ...(Array.isArray(rows) ? rows : [])];
  return createDocxTableBlob({ title, rows: normalizedRows, orientation });
}

export function createFiscalFallbackExport({ root, format, title, orientation = "portrait" } = {}) {
  const rows = collectFiscalReportRows(root, title);
  if (!rows.length) throw new Error("Anteprima documento non disponibile");
  if (format === "pdf") return createFiscalTextPdfBlob({ rows, title, orientation });
  if (format === "word") return createFiscalWordBlob({ rows, title, orientation });
  throw new Error("Formato documento non supportato");
}

const FISCAL_EMPTY_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

function fiscalNextPaint() {
  return new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
}

function fiscalSourcePages(root) {
  if (!root) return [];
  if (root.matches?.(".booking_fiscal_report")) return [root];
  return Array.from(root.querySelectorAll?.(".booking_fiscal_report") || []);
}

function fiscalWaitForStylesheet(link) {
  if (!link) return Promise.resolve();
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    link.addEventListener("load", finish, { once: true });
    link.addEventListener("error", finish, { once: true });
    window.setTimeout(finish, 1200);
  });
}

function fiscalPrintSnapshotPages({ snapshots, orientation = "portrait", title = "Documento fiscale" } = {}) {
  if (!Array.isArray(snapshots) || !snapshots.length) throw new Error("Anteprima documento non disponibile");

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.className = "vesta_fiscal_print_frame";
  document.body.appendChild(frame);

  const frameDocument = frame.contentDocument || frame.contentWindow?.document;
  if (!frameDocument) {
    frame.remove();
    throw new Error("Stampa documento non disponibile");
  }

  const landscape = orientation === "landscape";
  const pageWidth = landscape ? "297mm" : "210mm";
  const pageHeight = landscape ? "210mm" : "297mm";
  const pageSize = landscape ? "A4 landscape" : "A4 portrait";
  const objectUrls = snapshots.map((snapshot) => URL.createObjectURL(snapshot.blob));

  frameDocument.open();
  frameDocument.write(`<!doctype html><html><head><meta charset="utf-8"><title>${String(title || "Documento fiscale").replace(/[<&>]/g, "")}</title><style>
    @page { size: ${pageSize}; margin: 0; }
    html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    body { width: ${pageWidth}; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .vesta_fiscal_snapshot_page { width: ${pageWidth}; height: ${pageHeight}; margin: 0; padding: 0; overflow: hidden; break-after: page; page-break-after: always; background: #fff; }
    .vesta_fiscal_snapshot_page:last-child { break-after: auto; page-break-after: auto; }
    .vesta_fiscal_snapshot_page img { display: block; width: 100%; height: 100%; margin: 0; padding: 0; object-fit: fill; }
  </style></head><body></body></html>`);
  frameDocument.close();

  objectUrls.forEach((url) => {
    const page = frameDocument.createElement("div");
    page.className = "vesta_fiscal_snapshot_page";
    const image = frameDocument.createElement("img");
    image.alt = "";
    image.src = url;
    page.appendChild(image);
    frameDocument.body.appendChild(page);
  });

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
    frame.remove();
  };

  const runPrint = async () => {
    const frameWindow = frame.contentWindow;
    if (!frameWindow) {
      cleanup();
      return;
    }
    await Promise.all(Array.from(frameDocument.images || []).map((image) => fiscalWaitForImage(image, 5000)));
    frameWindow.focus();
    frameWindow.addEventListener?.("afterprint", cleanup, { once: true });
    frameWindow.print();
    window.setTimeout(cleanup, 5000);
  };

  window.setTimeout(() => { void runPrint(); }, 60);
}

function fiscalPrintDomFallback({ root, orientation = "portrait", title = "Documento fiscale" } = {}) {
  const sourcePages = fiscalSourcePages(root);
  if (!sourcePages.length) throw new Error("Anteprima documento non disponibile");

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.className = "vesta_fiscal_print_frame";
  document.body.appendChild(frame);

  const frameDocument = frame.contentDocument || frame.contentWindow?.document;
  if (!frameDocument) {
    frame.remove();
    throw new Error("Stampa documento non disponibile");
  }

  frameDocument.open();
  frameDocument.write("<!doctype html><html><head><meta charset=\"utf-8\"></head><body></body></html>");
  frameDocument.close();

  const base = frameDocument.createElement("base");
  base.href = document.baseURI;
  frameDocument.head.appendChild(base);
  const titleNode = frameDocument.createElement("title");
  titleNode.textContent = String(title || "Documento fiscale");
  frameDocument.head.appendChild(titleNode);

  const stylesheetPromises = [];
  document.head.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    const clone = node.cloneNode(true);
    frameDocument.head.appendChild(clone);
    if (clone.tagName === "LINK") stylesheetPromises.push(fiscalWaitForStylesheet(clone));
  });

  frameDocument.body.className = `vesta_fiscal_standalone utility_print_${orientation === "landscape" ? "landscape" : "portrait"}`;
  const printArea = frameDocument.createElement("div");
  printArea.className = "utility_preview_print_area booking_checkout_print_area";
  const pages = frameDocument.createElement("div");
  pages.className = "utility_print_pages";
  sourcePages.forEach((page) => pages.appendChild(page.cloneNode(true)));
  printArea.appendChild(pages);
  frameDocument.body.appendChild(printArea);

  const runPrint = async () => {
    const frameWindow = frame.contentWindow;
    if (!frameWindow) {
      frame.remove();
      return;
    }
    try {
      await Promise.all(stylesheetPromises);
      if (frameDocument.fonts?.ready) await frameDocument.fonts.ready;
      await Promise.all(Array.from(frameDocument.images || []).map((image) => fiscalWaitForImage(image, 2500)));
    } catch (_error) {
      // Fallback tecnico: la stampa resta comunque disponibile.
    }
    frameWindow.focus();
    frameWindow.print();
    window.setTimeout(() => frame.remove(), 1800);
  };

  window.setTimeout(() => { void runPrint(); }, 80);
}

function fiscalFontFaceCssText() {
  const chunks = [];
  const visitRules = (rules) => {
    for (const rule of Array.from(rules || [])) {
      const text = String(rule?.cssText || "");
      if (/^@font-face/i.test(text.trim())) chunks.push(text);
      if (rule?.cssRules) visitRules(rule.cssRules);
    }
  };
  for (const sheet of Array.from(document.styleSheets || [])) {
    try { visitRules(sheet.cssRules); } catch (_error) { /* Foglio non leggibile per CORS. */ }
  }
  return chunks.join("\n");
}

export function printFiscalPreviewRoot({ root, orientation = "portrait", title = "Documento fiscale" } = {}) {
  void (async () => {
    const sourcePages = fiscalSourcePages(root);
    if (!sourcePages.length) throw new Error("Anteprima documento non disponibile");

    const captureHost = document.createElement("div");
    captureHost.className = "app_root vesta_fiscal_export_capture";
    sourcePages.forEach((page) => captureHost.appendChild(page.cloneNode(true)));
    document.body.appendChild(captureHost);

    try {
      await fiscalNextPaint();
      if (document.fonts?.ready) await document.fonts.ready;
      await Promise.all(Array.from(captureHost.querySelectorAll("img")).map((image) => fiscalWaitForImage(image, 3000)));
      await fiscalNextPaint();

      const renderedPages = Array.from(captureHost.children);
      if (!renderedPages.length) throw new Error("Anteprima documento non disponibile");

      const frame = document.createElement("iframe");
      frame.setAttribute("aria-hidden", "true");
      frame.className = "vesta_fiscal_print_frame";
      document.body.appendChild(frame);

      const frameDocument = frame.contentDocument || frame.contentWindow?.document;
      if (!frameDocument) {
        frame.remove();
        throw new Error("Stampa documento non disponibile");
      }

      const landscape = orientation === "landscape";
      const pageWidth = landscape ? "297mm" : "210mm";
      const pageHeight = landscape ? "210mm" : "297mm";
      const pageSize = landscape ? "A4 landscape" : "A4 portrait";

      frameDocument.open();
      frameDocument.write(`<!doctype html><html><head><meta charset="utf-8"><title>${String(title || "Documento fiscale").replace(/[<&>]/g, "")}</title><style>
        @page { size: ${pageSize}; margin: 0; }
        html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
        body { width: ${pageWidth}; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .vesta_fiscal_exact_page { position: relative; width: ${pageWidth}; height: ${pageHeight}; margin: 0; padding: 0; overflow: hidden; break-after: page; page-break-after: always; background: #fff; }
        .vesta_fiscal_exact_page:last-child { break-after: auto; page-break-after: auto; }
      </style></head><body></body></html>`);
      frameDocument.close();

      const base = frameDocument.createElement("base");
      base.href = document.baseURI;
      frameDocument.head.appendChild(base);
      const fontCss = fiscalFontFaceCssText();
      if (fontCss) {
        const fontStyle = frameDocument.createElement("style");
        fontStyle.textContent = fontCss;
        frameDocument.head.appendChild(fontStyle);
      }

      for (const sourcePage of renderedPages) {
        const wrapper = frameDocument.createElement("div");
        wrapper.className = "vesta_fiscal_exact_page";
        const clone = sourcePage.cloneNode(true);
        await fiscalInlineComputedStyles(sourcePage, clone);
        clone.classList.remove("utility_screen_sheet");
        clone.style.setProperty("margin", "0", "important");
        clone.style.setProperty("box-shadow", "none", "important");
        wrapper.appendChild(frameDocument.importNode(clone, true));
        frameDocument.body.appendChild(wrapper);
      }

      const cleanup = () => frame.remove();
      const frameWindow = frame.contentWindow;
      if (!frameWindow) {
        cleanup();
        throw new Error("Stampa documento non disponibile");
      }

      await Promise.all(Array.from(frameDocument.images || []).map((image) => fiscalWaitForImage(image, 5000)));
      if (frameDocument.fonts?.ready) await frameDocument.fonts.ready;
      frameWindow.focus();
      frameWindow.addEventListener?.("afterprint", cleanup, { once: true });
      frameWindow.print();
      window.setTimeout(cleanup, 6000);
    } finally {
      captureHost.remove();
    }
  })().catch(async () => {
    try {
      const snapshots = await fiscalCreateSnapshots(root);
      fiscalPrintSnapshotPages({ snapshots, orientation, title });
    } catch (_error) {
      // L'errore di stampa non deve alterare l'overlay o lo stato del soggiorno.
    }
  });
}

function fiscalSafeComputedValue(value) {
  return /url\(/i.test(String(value || "")) ? "none" : String(value || "");
}

const FISCAL_CAPTURE_STYLE_PROPERTIES = [
  "display", "visibility", "position", "box-sizing", "float", "clear",
  "width", "height", "min-width", "min-height", "max-width", "max-height",
  "margin-top", "margin-right", "margin-bottom", "margin-left",
  "padding-top", "padding-right", "padding-bottom", "padding-left",
  "border-top-width", "border-right-width", "border-bottom-width", "border-left-width",
  "border-top-style", "border-right-style", "border-bottom-style", "border-left-style",
  "border-top-color", "border-right-color", "border-bottom-color", "border-left-color",
  "border-top-left-radius", "border-top-right-radius", "border-bottom-right-radius", "border-bottom-left-radius",
  "border-collapse", "border-spacing", "table-layout", "caption-side", "empty-cells",
  "background-color", "background-image", "background-repeat", "background-position", "background-size",
  "color", "opacity", "box-shadow", "filter",
  "font-family", "font-size", "font-style", "font-weight", "font-stretch", "font-variant",
  "line-height", "letter-spacing", "word-spacing", "text-align", "text-align-last", "text-indent",
  "text-transform", "text-decoration-line", "text-decoration-color", "text-decoration-style",
  "white-space", "word-break", "overflow-wrap", "hyphens", "vertical-align",
  "overflow", "overflow-x", "overflow-y", "text-overflow",
  "flex-direction", "flex-wrap", "flex-grow", "flex-shrink", "flex-basis",
  "align-items", "align-content", "align-self", "justify-content", "justify-items", "justify-self",
  "row-gap", "column-gap", "gap", "order",
  "grid-template-columns", "grid-template-rows", "grid-auto-columns", "grid-auto-rows", "grid-auto-flow",
  "grid-column-start", "grid-column-end", "grid-row-start", "grid-row-end",
  "object-fit", "object-position", "aspect-ratio",
  "transform", "transform-origin", "direction", "writing-mode",
];

async function fiscalInlineComputedStyles(sourceRoot, cloneRoot) {
  const sourceElements = [sourceRoot, ...Array.from(sourceRoot.querySelectorAll("*"))];
  const cloneElements = [cloneRoot, ...Array.from(cloneRoot.querySelectorAll("*"))];
  for (let index = 0; index < cloneElements.length; index += 1) {
    const source = sourceElements[index];
    const clone = cloneElements[index];
    if (!source || !clone || clone.nodeType !== 1) continue;
    const computed = window.getComputedStyle(source);
    const declarations = [];
    for (const property of Array.from(computed || [])) {
      const value = fiscalSafeComputedValue(computed.getPropertyValue(property));
      if (!value) continue;
      const priority = computed.getPropertyPriority(property);
      declarations.push(`${property}:${value}${priority ? " !important" : ""}`);
    }
    clone.setAttribute("style", declarations.join(";"));
  }
}

function fiscalBlobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Impossibile leggere la risorsa del documento"));
    reader.readAsDataURL(blob);
  });
}

async function fiscalImageDataUrl(image) {
  const source = image?.currentSrc || image?.src || "";
  if (!source) return FISCAL_EMPTY_IMAGE;
  if (source.startsWith("data:image/")) return source;
  try {
    const absoluteUrl = source.startsWith("blob:") ? source : new URL(source, document.baseURI).href;
    const sameOrigin = source.startsWith("blob:") || new URL(absoluteUrl).origin === window.location.origin;
    const response = await fetch(absoluteUrl, { credentials: sameOrigin ? "same-origin" : "omit", cache: "no-store" });
    if (!response.ok) return FISCAL_EMPTY_IMAGE;
    const blob = await response.blob();
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(blob.type || "")) return FISCAL_EMPTY_IMAGE;
    return fiscalBlobToDataUrl(blob);
  } catch (_error) {
    return FISCAL_EMPTY_IMAGE;
  }
}

async function fiscalSanitizeResources(sourcePage, clone) {
  const sourceImages = Array.from(sourcePage.querySelectorAll("img"));
  const cloneImages = Array.from(clone.querySelectorAll("img"));
  for (let index = 0; index < cloneImages.length; index += 1) {
    const dataUrl = await fiscalImageDataUrl(sourceImages[index]);
    cloneImages[index].removeAttribute("srcset");
    cloneImages[index].removeAttribute("sizes");
    cloneImages[index].removeAttribute("crossorigin");
    cloneImages[index].setAttribute("src", dataUrl || FISCAL_EMPTY_IMAGE);
  }
  clone.querySelectorAll("source, iframe, object, embed, video, audio").forEach((element) => element.remove());
  clone.querySelectorAll("svg image, svg feImage, svg use").forEach((element) => {
    element.removeAttribute("href");
    element.removeAttribute("xlink:href");
  });
  clone.querySelectorAll("[style]").forEach((element) => {
    const current = element.getAttribute("style") || "";
    if (/url\(/i.test(current)) element.setAttribute("style", current.replace(/url\([^)]*\)/gi, "none"));
  });
}

function fiscalLoadSvgImage(svg) {
  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);
  const encodedUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

  const load = (source) => new Promise((resolve, reject) => {
    const image = new Image();
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error("Timeout preparazione grafica documento"));
    }, 15000);
    image.onload = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(image);
    };
    image.onerror = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      reject(new Error("Impossibile preparare graficamente il documento"));
    };
    image.src = source;
  });

  return load(blobUrl)
    .catch(() => load(encodedUrl))
    .finally(() => URL.revokeObjectURL(blobUrl));
}

function fiscalCanvasFromImage(image, width, height, scale = 2) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d", { alpha: false, willReadFrequently: true });
  if (!context) throw new Error("Canvas non disponibile");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas;
}

async function fiscalRenderCloneToCanvas(clone, width, height, scale = 2) {
  const serialized = new XMLSerializer().serializeToString(clone);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><foreignObject x="0" y="0" width="${width}" height="${height}"><div xmlns="http://www.w3.org/1999/xhtml" style="width:${width}px;height:${height}px;background:#fff">${serialized}</div></foreignObject></svg>`;
  const image = await fiscalLoadSvgImage(svg);
  return fiscalCanvasFromImage(image, width, height, scale);
}

function fiscalDocumentCssText() {
  const chunks = [];
  for (const sheet of Array.from(document.styleSheets || [])) {
    try {
      for (const rule of Array.from(sheet.cssRules || [])) chunks.push(rule.cssText);
    } catch (_error) {
      // Fogli esterni non leggibili per CORS: il tentativo con stili calcolati resta quello principale.
    }
  }
  return chunks.join("\n").replace(/url\([^)]*\)/gi, "none");
}

async function fiscalRenderCloneWithDocumentCssToCanvas(clone, width, height, scale = 1.5) {
  const serialized = new XMLSerializer().serializeToString(clone);
  const css = fiscalDocumentCssText().replace(/]]>/g, "]]]]><![CDATA[>" );
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><foreignObject x="0" y="0" width="${width}" height="${height}"><div xmlns="http://www.w3.org/1999/xhtml" class="app_root" style="width:${width}px;height:${height}px;background:#fff"><style><![CDATA[${css}]]></style>${serialized}</div></foreignObject></svg>`;
  const image = await fiscalLoadSvgImage(svg);
  return fiscalCanvasFromImage(image, width, height, scale);
}

function fiscalDataUrlToBlob(dataUrl) {
  const [header, payload = ""] = String(dataUrl || "").split(",", 2);
  const mime = header.match(/^data:([^;]+)/i)?.[1] || "image/jpeg";
  const binary = window.atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mime });
}

function fiscalCanvasToJpegBlob(canvas) {
  return new Promise((resolve, reject) => {
    try {
      const context = canvas.getContext("2d", { willReadFrequently: true });
      context?.getImageData(0, 0, 1, 1);
      if (typeof canvas.toBlob === "function") {
        canvas.toBlob((blob) => {
          if (blob) { resolve(blob); return; }
          try { resolve(fiscalDataUrlToBlob(canvas.toDataURL("image/jpeg", 0.99))); }
          catch (error) { reject(error); }
        }, "image/jpeg", 0.99);
        return;
      }
      resolve(fiscalDataUrlToBlob(canvas.toDataURL("image/jpeg", 0.99)));
    } catch (error) {
      reject(error);
    }
  });
}

function fiscalStripAllUrlResources(clone) {
  clone.querySelectorAll("img").forEach((image) => {
    image.removeAttribute("srcset");
    image.removeAttribute("sizes");
    image.removeAttribute("crossorigin");
    image.setAttribute("src", FISCAL_EMPTY_IMAGE);
  });
  clone.querySelectorAll("source, iframe, object, embed, video, audio").forEach((element) => element.remove());
  clone.querySelectorAll("svg image, svg feImage, svg use").forEach((element) => {
    element.removeAttribute("href");
    element.removeAttribute("xlink:href");
  });
  clone.querySelectorAll("[style]").forEach((element) => {
    const style = element.getAttribute("style") || "";
    element.setAttribute("style", style.replace(/url\([^)]*\)/gi, "none"));
  });
}


function fiscalVisibleElement(element) {
  if (!element || element.nodeType !== 1) return false;
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity || 1) <= 0) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function fiscalNumber(value, fallback = 0) {
  const parsed = Number.parseFloat(String(value || ""));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function fiscalRoundedPath(context, x, y, width, height, radius) {
  const safeRadius = Math.max(0, Math.min(radius, width / 2, height / 2));
  context.beginPath();
  if (typeof context.roundRect === "function") {
    context.roundRect(x, y, width, height, safeRadius);
    return;
  }
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
}

function fiscalGradientFill(context, backgroundImage, x, y, width, height) {
  const value = String(backgroundImage || "");
  if (!/^linear-gradient\(/i.test(value)) return null;
  const colors = value.match(/(?:rgba?\([^)]*\)|#[0-9a-f]{3,8})/gi) || [];
  if (colors.length < 2) return null;
  const gradient = context.createLinearGradient(x, y, x + width, y + height);
  colors.forEach((color, index) => gradient.addColorStop(colors.length === 1 ? 0 : index / (colors.length - 1), color));
  return gradient;
}

function fiscalPaintBox(context, element, rootRect) {
  if (!fiscalVisibleElement(element)) return;
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  const x = rect.left - rootRect.left;
  const y = rect.top - rootRect.top;
  const width = rect.width;
  const height = rect.height;
  const radius = Math.max(
    fiscalNumber(style.borderTopLeftRadius),
    fiscalNumber(style.borderTopRightRadius),
    fiscalNumber(style.borderBottomRightRadius),
    fiscalNumber(style.borderBottomLeftRadius),
  );
  const gradient = fiscalGradientFill(context, style.backgroundImage, x, y, width, height);
  const background = String(style.backgroundColor || "");
  if (gradient || (background && background !== "transparent" && !/rgba\([^)]*,\s*0(?:\.0+)?\s*\)$/i.test(background))) {
    context.save();
    fiscalRoundedPath(context, x, y, width, height, radius);
    context.fillStyle = gradient || background;
    context.fill();
    context.restore();
  }

  const borders = [
    ["top", style.borderTopWidth, style.borderTopStyle, style.borderTopColor, x, y, x + width, y],
    ["right", style.borderRightWidth, style.borderRightStyle, style.borderRightColor, x + width, y, x + width, y + height],
    ["bottom", style.borderBottomWidth, style.borderBottomStyle, style.borderBottomColor, x, y + height, x + width, y + height],
    ["left", style.borderLeftWidth, style.borderLeftStyle, style.borderLeftColor, x, y, x, y + height],
  ];
  borders.forEach(([, rawWidth, borderStyle, color, x1, y1, x2, y2]) => {
    const borderWidth = fiscalNumber(rawWidth);
    if (borderWidth <= 0 || borderStyle === "none" || borderStyle === "hidden" || !color || color === "transparent") return;
    context.save();
    context.beginPath();
    context.lineWidth = borderWidth;
    context.strokeStyle = color;
    context.moveTo(x1, y1);
    context.lineTo(x2, y2);
    context.stroke();
    context.restore();
  });
}

function fiscalTransformText(text, transform) {
  if (transform === "uppercase") return text.toUpperCase();
  if (transform === "lowercase") return text.toLowerCase();
  if (transform === "capitalize") return text.replace(/(^|\s)(\S)/g, (_match, prefix, char) => `${prefix}${char.toUpperCase()}`);
  return text;
}

function fiscalPaintTextNodes(context, page, rootRect) {
  const walker = document.createTreeWalker(page, window.NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    const rawText = String(node.nodeValue || "");
    if (parent && /\S/.test(rawText) && fiscalVisibleElement(parent)) {
      const style = window.getComputedStyle(parent);
      const fontSize = Math.max(1, fiscalNumber(style.fontSize, 12));
      const fontWeight = style.fontWeight || "400";
      const fontStyle = style.fontStyle || "normal";
      const fontFamily = style.fontFamily || "Arial, sans-serif";
      const matches = Array.from(rawText.matchAll(/\S+/g));
      context.save();
      context.fillStyle = style.color || "#000000";
      context.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
      context.textBaseline = "top";
      if ("letterSpacing" in context) context.letterSpacing = style.letterSpacing || "0px";
      matches.forEach((match) => {
        const start = match.index || 0;
        const end = start + match[0].length;
        const range = document.createRange();
        try {
          range.setStart(node, start);
          range.setEnd(node, end);
          const rects = Array.from(range.getClientRects());
          const text = fiscalTransformText(match[0], style.textTransform);
          rects.forEach((rect) => {
            if (rect.width <= 0 || rect.height <= 0) return;
            const x = rect.left - rootRect.left;
            const y = rect.top - rootRect.top + Math.max(0, (rect.height - fontSize) / 2);
            context.fillText(text, x, y);
          });
        } catch (_error) {
          // Un nodo testuale non renderizzabile non deve bloccare l'intero documento.
        } finally {
          range.detach?.();
        }
      });
      context.restore();
    }
    node = walker.nextNode();
  }
}

function fiscalLoadDataImage(dataUrl) {
  return new Promise((resolve) => {
    if (!dataUrl || dataUrl === FISCAL_EMPTY_IMAGE) {
      resolve(null);
      return;
    }
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

async function fiscalPaintImages(context, page, rootRect) {
  const images = Array.from(page.querySelectorAll("img"));
  for (const sourceImage of images) {
    if (!fiscalVisibleElement(sourceImage)) continue;
    const rect = sourceImage.getBoundingClientRect();
    const style = window.getComputedStyle(sourceImage);
    const dataUrl = await fiscalImageDataUrl(sourceImage);
    const image = await fiscalLoadDataImage(dataUrl);
    if (!image || !image.naturalWidth || !image.naturalHeight) continue;
    const x = rect.left - rootRect.left;
    const y = rect.top - rootRect.top;
    const width = rect.width;
    const height = rect.height;
    const fit = String(style.objectFit || "fill");
    let drawX = x;
    let drawY = y;
    let drawWidth = width;
    let drawHeight = height;
    if (fit === "contain" || fit === "cover") {
      const ratio = fit === "cover"
        ? Math.max(width / image.naturalWidth, height / image.naturalHeight)
        : Math.min(width / image.naturalWidth, height / image.naturalHeight);
      drawWidth = image.naturalWidth * ratio;
      drawHeight = image.naturalHeight * ratio;
      drawX = x + (width - drawWidth) / 2;
      drawY = y + (height - drawHeight) / 2;
    }
    context.save();
    context.beginPath();
    context.rect(x, y, width, height);
    context.clip();
    context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
    context.restore();
  }
}

async function fiscalPaintDomToCanvas(page, scale = 2) {
  const rootRect = page.getBoundingClientRect();
  const width = Math.max(1, Math.round(rootRect.width || page.scrollWidth || page.offsetWidth || 794));
  const height = Math.max(1, Math.round(rootRect.height || page.scrollHeight || page.offsetHeight || 1123));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d", { alpha: false, willReadFrequently: true });
  if (!context) throw new Error("Canvas non disponibile");
  context.save();
  context.scale(scale, scale);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);

  fiscalPaintBox(context, page, rootRect);
  Array.from(page.querySelectorAll("*")).forEach((element) => fiscalPaintBox(context, element, rootRect));
  await fiscalPaintImages(context, page, rootRect);
  fiscalPaintTextNodes(context, page, rootRect);
  context.restore();
  return canvas;
}

async function fiscalSnapshotPage(page) {
  const rect = page.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width || page.scrollWidth || page.offsetWidth || 794));
  const height = Math.max(1, Math.round(rect.height || page.scrollHeight || page.offsetHeight || 1123));
  let lastError = null;

  try {
    const canvas = await fiscalPaintDomToCanvas(page, 2);
    return { blob: await fiscalCanvasToJpegBlob(canvas), width: canvas.width, height: canvas.height };
  } catch (error) {
    lastError = error;
  }

  const clone = page.cloneNode(true);
  await fiscalInlineComputedStyles(page, clone);
  await fiscalSanitizeResources(page, clone);
  for (const scale of [2, 1.5, 1]) {
    try {
      const canvas = await fiscalRenderCloneToCanvas(clone, width, height, scale);
      return { blob: await fiscalCanvasToJpegBlob(canvas), width: canvas.width, height: canvas.height };
    } catch (error) {
      lastError = error;
    }
  }

  const cssClone = page.cloneNode(true);
  await fiscalSanitizeResources(page, cssClone);
  for (const scale of [1.5, 1, 0.75]) {
    try {
      const canvas = await fiscalRenderCloneWithDocumentCssToCanvas(cssClone, width, height, scale);
      return { blob: await fiscalCanvasToJpegBlob(canvas), width: canvas.width, height: canvas.height };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Impossibile creare l'immagine del documento");
}

function fiscalWaitForImage(image, timeoutMs = 1600) {
  if (!image || image.complete) return Promise.resolve();
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, timeoutMs);
    image.addEventListener("load", finish, { once: true });
    image.addEventListener("error", finish, { once: true });
  });
}

async function fiscalCreateSnapshots(root) {
  if (!root) throw new Error("Anteprima documento non disponibile");
  const sourcePages = fiscalSourcePages(root);
  if (!sourcePages.length) throw new Error("Anteprima documento non disponibile");

  const host = document.createElement("div");
  host.className = "app_root vesta_fiscal_export_capture";
  sourcePages.forEach((page) => host.appendChild(page.cloneNode(true)));
  document.body.appendChild(host);
  try {
    await fiscalNextPaint();
    if (document.fonts?.ready) await document.fonts.ready;
    await Promise.all(Array.from(host.querySelectorAll("img")).map((image) => fiscalWaitForImage(image)));
    await fiscalNextPaint();
    const snapshots = [];
    for (const page of Array.from(host.children)) snapshots.push(await fiscalSnapshotPage(page));
    if (!snapshots.length) throw new Error("Anteprima documento non disponibile");
    return snapshots;
  } finally {
    host.remove();
  }
}

function fiscalConcatBytes(...chunks) {
  const length = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(length);
  let offset = 0;
  chunks.forEach((chunk) => { result.set(chunk, offset); offset += chunk.length; });
  return result;
}

async function fiscalSnapshotPdf(snapshots, orientation) {
  const encoder = new TextEncoder();
  const landscape = orientation === "landscape";
  const pageWidth = landscape ? 841.89 : 595.28;
  const pageHeight = landscape ? 595.28 : 841.89;
  const objectCount = 2 + snapshots.length * 3;
  const objects = new Array(objectCount + 1);
  const pageIds = [];
  for (let index = 0; index < snapshots.length; index += 1) {
    const pageId = 3 + index * 3;
    const contentId = pageId + 1;
    const imageId = pageId + 2;
    pageIds.push(pageId);
    const jpegBytes = new Uint8Array(await snapshots[index].blob.arrayBuffer());
    objects[imageId] = fiscalConcatBytes(
      encoder.encode(`<< /Type /XObject /Subtype /Image /Width ${snapshots[index].width} /Height ${snapshots[index].height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`),
      jpegBytes,
      encoder.encode("\nendstream")
    );
    const content = `q\n${pageWidth.toFixed(2)} 0 0 ${pageHeight.toFixed(2)} 0 0 cm\n/Im${index + 1} Do\nQ`;
    objects[contentId] = encoder.encode(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
    objects[pageId] = encoder.encode(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth.toFixed(2)} ${pageHeight.toFixed(2)}] /Resources << /XObject << /Im${index + 1} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`);
  }
  objects[1] = encoder.encode("<< /Type /Catalog /Pages 2 0 R >>");
  objects[2] = encoder.encode(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`);
  const chunks = [];
  const header = encoder.encode("%PDF-1.4\n%VestaMNG\n");
  chunks.push(header);
  let offset = header.length;
  const offsets = new Array(objectCount + 1).fill(0);
  for (let id = 1; id <= objectCount; id += 1) {
    const start = encoder.encode(`${id} 0 obj\n`);
    const end = encoder.encode("\nendobj\n");
    offsets[id] = offset;
    chunks.push(start, objects[id], end);
    offset += start.length + objects[id].length + end.length;
  }
  const xrefOffset = offset;
  let xref = `xref\n0 ${objectCount + 1}\n0000000000 65535 f \n`;
  for (let id = 1; id <= objectCount; id += 1) xref += `${String(offsets[id]).padStart(10, "0")} 00000 n \n`;
  xref += `trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  chunks.push(encoder.encode(xref));
  return new Blob(chunks, { type: "application/pdf" });
}

export async function createFiscalVisualExportBlob({ root, format, orientation = "portrait" } = {}) {
  const snapshots = await fiscalCreateSnapshots(root);
  if (!snapshots.length) throw new Error("Anteprima documento non disponibile");
  if (format === "pdf") return fiscalSnapshotPdf(snapshots, orientation);
  if (format === "word") return createDocxImagePagesBlob(snapshots, orientation);
  throw new Error("Formato documento non supportato");
}
